import React, {
  FunctionComponent,
  ReactElement,
  useState,
  useCallback,
} from 'react';
import { useNavigate } from "react-router-dom";

// Material UI components
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams, GridSelectionModel } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import LinearProgress from '@mui/material/LinearProgress';

// Types and constants
import { FirmwareModel } from '../../../../shared/types';
import { FIRMWARE_BUTTON_IDS, FIRMWARE_TABLE_COLUMNS, PAGE_SIZE } from '../../../../shared/constants';

// Components
import DeleteFirmwareDialog from '../DeleteFirmwareDialog/DeleteFirmwareDialog'
import Separator from '../../../Separator/Separator';
import Status from '../../../Status/Status';

type FirmwareTableProps = {
  firmwareList: FirmwareModel[];
  openDeleteFirmwareDialog: boolean;
  firmwaresToDelete: FirmwareModel[];
  deleteHandler: (firmwareIds: string[]) => void;
  deleteFirmware: () => void;
  handleClose: () => void;
}

const FirmwareTable: FunctionComponent<FirmwareTableProps> = (props: FirmwareTableProps): ReactElement => {
  const {
    firmwareList,
    openDeleteFirmwareDialog,
    firmwaresToDelete,
    deleteHandler,
    deleteFirmware,
    handleClose,
  } = props;

  const [selectedFirmwareIds, setSelectedFirmwareIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const navigate = useNavigate();

  const goTo = (route: string) => {
    navigate(route);
  }

  const firmwareSellectionHandler = (newSelectionModel: GridSelectionModel) => {
    setSelectedFirmwareIds(newSelectionModel as string[]);
  };

  const pageChangeHandler = (newPage: number) => setCurrentPage(newPage)

  const isRowSelectable = useCallback((params: GridRowParams) => {
    const id = params.row.id;
    const firmwareIds = firmwareList.map((f) => f.id);

    const n = currentPage + 1;
    const startIndex = currentPage * PAGE_SIZE;
    const endIndex = n * PAGE_SIZE;
    const idIndex = firmwareIds.indexOf(id);

    return idIndex >= startIndex && idIndex < endIndex;

  }, [firmwareList, currentPage]);

  const firmwareTableColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      headerClassName: 'table-header',
      renderCell: (params: GridRenderCellParams<FirmwareModel>) => (
        <>
          <Status isActive={!!params.row.status} />
          <Link
            underline="hover"
            onClick={() => goTo(`/firmware/${params.row?.id}`)}
          >
            <span>{params.value}</span>
          </Link>
        </>

      ),
    },
    ...FIRMWARE_TABLE_COLUMNS
  ];

  return (
    <div data-testid='firmwareTable-container' className='align-center bottom-padding'>
      <div>
        <Button
          data-testid={FIRMWARE_BUTTON_IDS.CREATE_BTN}
          variant='outlined'
          onClick={() => goTo('/firmware/new')}
          startIcon={<AddIcon />}
        >
          Create Firmware
        </Button>
        <Separator direction='vertical' />
        {
          selectedFirmwareIds.length > 0 && (
            <Button
              data-testid={FIRMWARE_BUTTON_IDS.DELETE_BTN}
              color='error'
              variant='outlined'
              startIcon={<DeleteIcon />}
              onClick={() => deleteHandler(selectedFirmwareIds)}
            >
              Delete
            </Button>
          )
        }
      </div>

      <Separator />
      <Separator />

      <DataGrid
        sx={{ border: 'none' }}
        rows={firmwareList}
        columns={firmwareTableColumns}
        density='comfortable'
        hideFooterSelectedRowCount
        autoHeight
        components={{
          LoadingOverlay: LinearProgress,
        }}
        loading={firmwareList.length === 0}
        pageSize={PAGE_SIZE}
        checkboxSelection
        pagination
        onSelectionModelChange={firmwareSellectionHandler}        
        onPageChange={pageChangeHandler}
        isRowSelectable={isRowSelectable}
      />

      {
        firmwaresToDelete
        && (
          <DeleteFirmwareDialog
            openDeleteFirmwareDialog={openDeleteFirmwareDialog}
            firmwaresToDelete={firmwaresToDelete}
            handleClose={handleClose}
            deleteFirmware={deleteFirmware}
          />
        )
      }
    </div>
  );
};

export default FirmwareTable;
